import { useCallback } from "react";
import { CherryPayApi } from "../api/models";
import { useApiClient } from "./useApiClient";
import { useToast } from "./useToast";

interface UseNotificationRevertOptions {
  businessId: string;
  selectedNotification: CherryPayApi.CherryPassNotificationSchedule | null;
  setSelectedNotification: (
    notification: CherryPayApi.CherryPassNotificationSchedule | null
  ) => void;
  refresh: () => void;
  showConfirmationDialog: (
    title: string,
    message: string,
    confirmText: string,
    cancelText: string
  ) => Promise<{ ok: boolean }>;
  resetFilters: () => void;
}

export const useNotificationRevert = (
  options: UseNotificationRevertOptions
) => {
  const {
    businessId,
    selectedNotification,
    setSelectedNotification,
    refresh,
    showConfirmationDialog,
    resetFilters,
  } = options;

  const apiClient = useApiClient();
  const { showInfoToast, showErrorToast, showSuccessToast } = useToast();

  return useCallback(
    async (notification: CherryPayApi.CherryPassNotificationSchedule) => {
      if (!notification.Meta?.Id) {
        showErrorToast(
          "Unable to revert notification: missing notification ID"
        );
        return;
      }

      showConfirmationDialog(
        "Are you sure you want to revert?",
        "This action will cancel the scheduled delivery and allow you to re-edit and reschedule the message. Click Confirm to proceed or Cancel to go back.",
        "Confirm",
        "Cancel"
      ).then(({ ok }) => {
        if (ok && notification.Meta?.Id) {
          showInfoToast("Reverting notification to draft...");

          apiClient
            .draftScheduledCherryPassNotificationSchedule(
              businessId,
              notification.Meta.Id
            )
            .then((result) => {
              if (result.ok) {
                showSuccessToast("Notification reverted to draft successfully");

                // Reset filters so the reverted notification appears in the list
                resetFilters();

                // Update selected notification if it was the reverted one
                if (selectedNotification?.Meta?.Id === notification.Meta?.Id) {
                  setSelectedNotification(result.data);
                }

                // Refresh the list to show the updated status
                refresh();
              } else {
                showErrorToast(
                  "Failed to revert notification: " + result.message
                );
              }
            })
            .catch((error) => {
              showErrorToast("Failed to revert notification");
              console.error("Revert notification error:", error);
            });
        }
      });
    },
    [
      businessId,
      apiClient,
      showErrorToast,
      showSuccessToast,
      showInfoToast,
      showConfirmationDialog,
      selectedNotification,
      setSelectedNotification,
      refresh,
      resetFilters,
    ]
  );
};
